import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FirebaseResponseCode,
  confirmNewPassword,
  resentPasswordResetLink,
  validatePasswordResetLink,
} from "../firebase";
import { ROUTES } from "../routes";

type Props = {
  confirmButtonText: string;
  onConfirm: (newPassword: string) => Promise<void>; // Fix: return type is a Promise<boolean>
};

// Password validation utility
const validatePassword = (password: string): string[] => {
  const errors: string[] = [];
  
  if (password.length < 8) {
    errors.push("Password must be at least 8 characters long");
  }
  if (!/[A-Z]/.test(password)) {
    errors.push("Password must contain at least one uppercase letter");
  }
  if (!/[a-z]/.test(password)) {
    errors.push("Password must contain at least one lowercase letter");
  }
  if (!/\d/.test(password)) {
    errors.push("Password must contain at least one number");
  }
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    errors.push("Password must contain at least one special character");
  }
  
  return errors;
};

export default function PasswordSet({ confirmButtonText, onConfirm }: Props) {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);

  const handlePasswordChange = (password: string) => {
    setNewPassword(password);
    const errors = validatePassword(password);
    setPasswordErrors(errors);
    setMessage("");
  };

  const handlePasswordReset = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    // Validate password strength
    const errors = validatePassword(newPassword);
    if (errors.length > 0) {
      setMessage("Please fix the password requirements below.");
      setPasswordErrors(errors);
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage("Passwords do not match.");
      return;
    }

    if (newPassword.trim().length === 0) {
      setMessage("Password cannot be empty.");
      return;
    }

    try {
      await onConfirm(newPassword);
    } catch (error) {
      setMessage("Failed to set password. Please try again.");
    }
  };

  return (
    <div>
      {message && <p style={{ color: 'red' }}>{message}</p>}
      
      <form className="login-form" onSubmit={handlePasswordReset}>
        <div className="input-group">
          <label>New Password</label>
          <input
            type="password"
            value={newPassword}
            onChange={(e) => handlePasswordChange(e.target.value)}
            required
            minLength={8}
          />
          
          {/* Password strength indicator */}
          {newPassword && (
            <div style={{ marginTop: '5px' }}>
              <div style={{ fontSize: '0.8em', color: passwordErrors.length === 0 ? 'green' : 'orange' }}>
                Password strength: {passwordErrors.length === 0 ? 'Strong' : 'Weak'}
              </div>
              {passwordErrors.length > 0 && (
                <ul style={{ fontSize: '0.7em', color: 'red', margin: '5px 0', paddingLeft: '20px' }}>
                  {passwordErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
        <div className="input-group">
          <label>Confirm New Password</label>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
        </div>
        <button type="submit">{confirmButtonText}</button>
      </form>
    </div>
  );
}
